import { IMPORT_SLICE } from '@itc/shared';
import { z } from 'zod';

// Importing ITCBot's history (plan 5F, R4, ADR 0019): `config/sync_history.json` into `members_snapshots` and
// `reports/reports_stats.json` into `members_role_samples`, as pure functions: what one old entry becomes, and what a slice of
// them does against what is stored. Neither file has ids, so a row is named by its place in the file (`n`), which is
// all a refusal says, and is kept by what makes it itself: a snapshot by its moment, a sample by its role and moment.
// A repeat is skipped by that key, so running an import twice creates nothing.

export const SNAPSHOT_FILE = 'config/sync_history.json';
export const STATS_FILE = 'reports/reports_stats.json';

/** The most any count may be, as a sanity limit on what a file claims. */
export const MAX_COUNT = 10_000_000;

/** What reading one old entry says: a row to create under a key, a row to leave, or a reason to refuse it. */
export type KeyedRead<Fields> = { kind: 'row'; key: string; fields: Fields } | { kind: 'skip' } | { kind: 'refuse'; reason: string };

export interface KeyedRow<Fields> {
  id: string;
  key: string;
  fields: Fields;
}

export interface KeyedPlan<Fields> {
  create: KeyedRow<Fields>[];
  skipped: number;
  refused: { id: string; reason: string }[];
}

/**
 * The shape every import of a keyed row has: refused if it can't be read, skipped if it is left alone or its key is already held (stored, or
 * earlier in the slice, which is what makes a repeat safe), created otherwise. `taken` is the keys stored plus, in a dry run, those
 * that earlier slices would create. It isn't changed.
 */
export function planKeyed<Raw extends { id: string }, Fields>(
  rows: readonly Raw[],
  read: (raw: Raw) => KeyedRead<Fields>,
  taken: ReadonlySet<string>,
  /** What a refusal calls a row. Its id, unless that isn't one that is safe to repeat. */
  name: (raw: Raw) => string = (raw) => raw.id,
): KeyedPlan<Fields> {
  const plan: KeyedPlan<Fields> = { create: [], skipped: 0, refused: [] };
  const seen = new Set(taken);
  for (const raw of rows) {
    const result = read(raw);
    if (result.kind === 'refuse') {
      plan.refused.push({ id: name(raw), reason: result.reason });
      continue;
    }
    if (result.kind === 'skip' || seen.has(result.key)) {
      plan.skipped += 1;
      continue;
    }
    seen.add(result.key);
    plan.create.push({ id: raw.id, key: result.key, fields: result.fields });
  }
  return plan;
}

/** Splits rows into slices of at most `limit`, in order. */
export function sliceEvery<T>(rows: readonly T[], limit: number = IMPORT_SLICE): T[][] {
  const slices: T[][] = [];
  for (let start = 0; start < rows.length; start += limit) slices.push(rows.slice(start, start + limit));
  return slices;
}

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * An old moment: an ISO string, or Python's `isoformat()` of one, with up to six digits of fraction. One with no zone was written
 * by `utcnow()`, so it is UTC. Null for anything else, and for a year outside 2000 to 2100.
 */
export function readMoment(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const text = value.trim().replace(' ', 'T');
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/i.test(text)) return null;
  const at = new Date(/(Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`);
  return Number.isNaN(at.getTime()) || at.getUTCFullYear() < 2000 || at.getUTCFullYear() > 2100 ? null : at;
}

// --- sync_history.json -------------------------------------------------------------------------

/** One entry of ITCBot's `sync_history.json`, numbered by its place. Every field is as the file had it. */
export const SnapshotImportRow = z.object({
  id: z.string().max(12),
  timestamp: z.unknown().optional(),
  rsi_members: z.unknown().optional(),
  linked_members: z.unknown().optional(),
  verified_members: z.unknown().optional(),
  threat_members: z.unknown().optional(),
  redacted_org_members: z.unknown().optional(),
  type_member: z.unknown().optional(),
  type_affiliate: z.unknown().optional(),
});
export type SnapshotImportRow = z.infer<typeof SnapshotImportRow>;

export const SNAPSHOT_COUNTS = ['rsi_members', 'linked_members', 'verified_members', 'threat_members', 'redacted_org_members', 'type_member', 'type_affiliate'] as const;

export interface SnapshotFields {
  at: Date;
  rsi_members: number;
  linked_members: number;
  verified_members: number;
  threat_members: number;
  redacted_org_members: number;
  type_member: number;
  type_affiliate: number;
}

/** The key of a snapshot: its moment, to the millisecond, which is all a record keeps. */
export const momentKey = (at: Date): string => String(at.getTime());

/**
 * R4: `timestamp` is kept as `at`. A count the file leaves out is 0, as ITCBot's own chart read it (`entry.get(…, 0)`); one that is there
 * and isn't a whole number from 0 up is refused, saying which, and not repeated. A snapshot already stored under the same moment is skipped.
 */
export function readSnapshot(raw: SnapshotImportRow): KeyedRead<SnapshotFields> {
  const at = readMoment(raw.timestamp);
  if (at === null) return { kind: 'refuse', reason: 'Its timestamp can’t be read' };
  const fields: SnapshotFields = { at, rsi_members: 0, linked_members: 0, verified_members: 0, threat_members: 0, redacted_org_members: 0, type_member: 0, type_affiliate: 0 };
  for (const name of SNAPSHOT_COUNTS) {
    const value = raw[name];
    if (value === undefined || value === null) continue;
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > MAX_COUNT) return { kind: 'refuse', reason: `Its ${name} isn’t a whole number from 0 to ${String(MAX_COUNT)}` };
    fields[name] = value;
  }
  return { kind: 'row', key: momentKey(at), fields };
}

/** The key a snapshot would be stored under, or null for one that can't be read. For the browser, which tells a dry run what earlier slices hold. */
export function snapshotKey(raw: SnapshotImportRow): string | null {
  const read = readSnapshot(raw);
  return read.kind === 'row' ? read.key : null;
}

/** The file as rows: one for each entry, an entry that isn't an object having nothing to read. Null if the file isn't a list. */
export function snapshotRows(file: unknown): SnapshotImportRow[] | null {
  if (!Array.isArray(file)) return null;
  return file.map((entry: unknown, index) => {
    const own = isObject(entry) ? entry : {};
    return {
      id: String(index + 1),
      timestamp: own['timestamp'],
      rsi_members: own['rsi_members'],
      linked_members: own['linked_members'],
      verified_members: own['verified_members'],
      threat_members: own['threat_members'],
      redacted_org_members: own['redacted_org_members'],
      type_member: own['type_member'],
      type_affiliate: own['type_affiliate'],
    };
  });
}

// --- reports_stats.json ------------------------------------------------------------------------

/** One `[timestamp, role, count]` of a guild's list in ITCBot's `reports_stats.json`, numbered by its place. */
export const SampleImportRow = z.object({
  id: z.string().max(12),
  guild: z.string().max(40),
  /** Seconds since 1970, as Python's `time.time()` gave them. */
  ts: z.unknown().optional(),
  role: z.unknown().optional(),
  count: z.unknown().optional(),
});
export type SampleImportRow = z.infer<typeof SampleImportRow>;

export interface SampleFields {
  role_id: string;
  at: Date;
  count: number;
}

const SNOWFLAKE = /^[0-9]{1,32}$/;

/** What a role id told as a number past 2^53 is refused with: whatever read the file has already rounded it. */
export const UNSAFE_ID = 'Its role id was written as a number too large to be exact, so the export rounded it. Export again with the current exporter';

/** A role id as ITCBot kept it: a string, or an integer (Python wrote `role.id` as one). An integer past 2^53 can't be trusted. */
export function readRoleId(value: unknown): { ok: true; id: string } | { ok: false; reason: string } {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return SNOWFLAKE.test(trimmed) ? { ok: true, id: BigInt(trimmed).toString() } : { ok: false, reason: 'Its role id isn’t a Discord id' };
  }
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return Number.isSafeInteger(value) ? { ok: true, id: String(value) } : { ok: false, reason: UNSAFE_ID };
  return { ok: false, reason: 'Its role id isn’t a Discord id' };
}

export const sampleKey = (roleId: string, at: Date): string => `${roleId}|${momentKey(at)}`;

/**
 * R4. Only this guild's: a row of another guild, or any row when this server has no guild set up, is refused. A reading older than the retention is
 * skipped, as the next prune would delete it. The moment is kept to the second, as the file has it. A row already stored under the same
 * role and moment is skipped.
 */
export function readSample(raw: SampleImportRow, options: { guild: string | undefined; cutoff: Date }): KeyedRead<SampleFields> {
  if (options.guild === undefined) return { kind: 'refuse', reason: 'This server has no Discord guild set up, so it can’t tell which readings are its own' };
  if (raw.guild !== options.guild) return { kind: 'refuse', reason: 'It belongs to another Discord server, not this one’s' };
  const role = readRoleId(raw.role);
  if (!role.ok) return { kind: 'refuse', reason: role.reason };
  const seconds = raw.ts;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return { kind: 'refuse', reason: 'Its timestamp isn’t a time' };
  const at = new Date(Math.floor(seconds) * 1000);
  if (at.getUTCFullYear() < 2000 || at.getUTCFullYear() > 2100) return { kind: 'refuse', reason: 'Its timestamp isn’t a time' };
  const count = raw.count;
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 0 || count > MAX_COUNT) return { kind: 'refuse', reason: `Its count isn’t a whole number from 0 to ${String(MAX_COUNT)}` };
  if (at.getTime() < options.cutoff.getTime()) return { kind: 'skip' };
  return { kind: 'row', key: sampleKey(role.id, at), fields: { role_id: role.id, at, count } };
}

/** The key a reading would be stored under, or null if it can't be read or would be skipped for age. For the browser's dry run. */
export function sampleKeyOf(raw: SampleImportRow, options: { guild: string | undefined; cutoff: Date }): string | null {
  const read = readSample(raw, options);
  return read.kind === 'row' ? read.key : null;
}

export interface FlatStats {
  /** This guild's readings, in the file's order. */
  rows: SampleImportRow[];
  /** Readings under any other guild: counted and never sent. */
  otherGuilds: number;
}

/**
 * The file as rows: `{ "<guild id>": [[timestamp, role, count], …] }`. Only `guild`'s list is sent. An entry that isn't a triple is sent with
 * nothing to read, so it is refused and listed. Null when the file isn't a map of lists.
 */
export function flattenStats(file: unknown, guild: string | null): FlatStats | null {
  if (!isObject(file)) return null;
  const out: FlatStats = { rows: [], otherGuilds: 0 };
  for (const [owner, list] of Object.entries(file)) {
    if (!Array.isArray(list)) continue;
    if (guild === null || owner !== guild) {
      out.otherGuilds += list.length;
      continue;
    }
    for (const [index, entry] of list.entries()) {
      const triple = Array.isArray(entry) && entry.length === 3 ? entry : [undefined, undefined, undefined];
      out.rows.push({ id: String(index + 1), guild: owner, ts: triple[0], role: triple[1], count: triple[2] });
    }
  }
  return out;
}

/** The earlier slices' keys a dry run sends with a slice. At most one for each row in it. */
export const EARLIER_LIMIT = IMPORT_SLICE;
